import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle2, XCircle, IndianRupee, Loader2, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import {
    fetchMyCollections,
    updateMyCollection,
    withdrawMyCollection,
    type MyCollection,
    type CollectionStatus,
} from '@/app/services/receiptService';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCard } from '@/components/ui/MobileCard';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';

const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;

const errorMessage = (err: unknown, fallback: string) => {
    if (axios.isAxiosError(err)) {
        return (err.response?.data as { detail?: string })?.detail ?? fallback;
    }
    return fallback;
};

/** How each state reads to the agent — plain language, no system jargon. */
const STATUS_META: Record<
    CollectionStatus,
    { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
    pending: { label: 'Awaiting office', variant: 'warning' },
    approved: { label: 'Confirmed', variant: 'success' },
    withdrawn: { label: 'Withdrawn', variant: 'secondary' },
    rejected: { label: 'Rejected', variant: 'destructive' },
    cancelled: { label: 'Cancelled', variant: 'destructive' },
};

/**
 * Everything this agent has recorded, and where each item stands.
 *
 * Until now agents had no way to see their own collections at all — they tapped
 * save and the entry vanished. That mattered less when receipts posted instantly;
 * now that the office confirms them, the agent needs to see what is still waiting
 * and be able to fix their own mistakes before anyone acts on them.
 */
export default function MyCollectionsPage() {
    const queryClient = useQueryClient();
    const [filter, setFilter] = useState<CollectionStatus | undefined>(undefined);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editAmount, setEditAmount] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['my-collections', filter],
        queryFn: () => fetchMyCollections(filter),
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['my-collections'] });

    const updateMutation = useMutation({
        mutationFn: ({ id, amount }: { id: number; amount: number }) =>
            updateMyCollection(id, { amount }),
        onSuccess: () => {
            toast.success('Amount corrected.');
            setEditingId(null);
            refresh();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not update. Please try again.')),
    });

    const withdrawMutation = useMutation({
        mutationFn: (id: number) => withdrawMyCollection(id),
        onSuccess: () => {
            toast.success('Collection withdrawn.');
            refresh();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not withdraw. Please try again.')),
    });

    const collections = data?.items ?? [];
    const pendingTotal = collections
        .filter((c) => c.status === 'pending')
        .reduce((sum, c) => sum + Number(c.amount), 0);

    const startEdit = (c: MyCollection) => {
        setEditingId(c.id);
        setEditAmount(String(c.amount));
    };

    const saveEdit = (c: MyCollection) => {
        const parsed = Number(editAmount);
        if (!parsed || parsed <= 0) {
            toast.error('Enter a valid amount');
            return;
        }
        updateMutation.mutate({ id: c.id, amount: parsed });
    };

    // pb-tab-bar, not pb-20: the tab bar is fixed and ~90px tall including the
    // safe area, so 80px leaves the last card clipped behind it.
    return (
        <div className="pb-tab-bar">
            <MobileHeader title="My Collections" />

            <div className="p-4 space-y-4">
                {pendingTotal > 0 && (
                    <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3">
                        <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-yellow-500" />
                            <span className="font-medium">{inr(pendingTotal)} awaiting office</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Hand this cash to the office. They will check each entry and confirm it.
                        </p>
                    </div>
                )}

                <div className="flex gap-2 overflow-x-auto">
                    {(
                        [
                            [undefined, 'All'],
                            ['pending', 'Awaiting'],
                            ['approved', 'Confirmed'],
                        ] as [CollectionStatus | undefined, string][]
                    ).map(([value, label]) => (
                        <button
                            key={label}
                            onClick={() => setFilter(value)}
                            className={`px-3 py-1.5 text-sm rounded-full whitespace-nowrap ${
                                filter === value
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-secondary text-secondary-foreground'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        <MobileSkeleton className="h-24 w-full" />
                        <MobileSkeleton className="h-24 w-full" />
                    </div>
                ) : collections.length === 0 ? (
                    <MobileEmptyState
                        icon={<IndianRupee className="h-10 w-10" />}
                        title="Nothing recorded yet"
                        description="Cash you collect will appear here until the office confirms it."
                    />
                ) : (
                    <div className="space-y-3">
                        {collections.map((c) => {
                            const meta = STATUS_META[c.status];
                            const editable = c.status === 'pending';
                            return (
                                <MobileCard key={c.id}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-medium truncate">{c.party_name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {c.collection_date}
                                                {c.voucher_number ? ` · ${c.voucher_number}` : ''}
                                            </p>
                                        </div>
                                        <MobileBadge variant={meta.variant}>{meta.label}</MobileBadge>
                                    </div>

                                    {editingId === c.id ? (
                                        <div className="mt-3 space-y-2">
                                            <MobileInput
                                                type="number"
                                                inputMode="decimal"
                                                value={editAmount}
                                                onChange={(e) => setEditAmount(e.target.value)}
                                                autoFocus
                                            />
                                            <div className="flex gap-2">
                                                <MobileButton
                                                    onClick={() => saveEdit(c)}
                                                    disabled={updateMutation.isPending}
                                                    className="flex-1"
                                                >
                                                    {updateMutation.isPending && (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    )}
                                                    Save
                                                </MobileButton>
                                                <MobileButton
                                                    variant="secondary"
                                                    onClick={() => setEditingId(null)}
                                                    className="flex-1"
                                                >
                                                    Cancel
                                                </MobileButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-2 flex items-center justify-between">
                                            <span className="text-lg font-semibold">{inr(c.amount)}</span>
                                            {editable && (
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => startEdit(c)}
                                                        className="p-2 rounded-md active:bg-accent"
                                                        aria-label="Correct amount"
                                                    >
                                                        <Pencil className="h-4 w-4 text-muted-foreground" />
                                                    </button>
                                                    <button
                                                        onClick={() => withdrawMutation.mutate(c.id)}
                                                        disabled={withdrawMutation.isPending}
                                                        className="p-2 rounded-md active:bg-accent"
                                                        aria-label="Withdraw"
                                                    >
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* The office changed this from what the agent entered. */}
                                    {c.was_amended && Number(c.amount) !== Number(c.declared_amount) && (
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            You entered {inr(c.declared_amount)} — corrected to{' '}
                                            {inr(c.amount)}
                                        </p>
                                    )}

                                    {c.status === 'rejected' && c.rejection_reason && (
                                        <p className="mt-2 text-xs text-destructive flex items-start gap-1">
                                            <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            {c.rejection_reason}
                                        </p>
                                    )}

                                    {c.status === 'approved' && (
                                        <p className="mt-2 text-xs text-green-500 flex items-center gap-1">
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            Confirmed by the office
                                        </p>
                                    )}
                                </MobileCard>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
